import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, TextField, IconButton, Chip, CircularProgress, Tooltip, Button,
  Alert, FormControlLabel, Switch, MenuItem, Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { adminApi } from '../../api';

const CATEGORY_COLORS = {
  storage: { bg: '#002233', color: '#00bcd4' },
  lcm:     { bg: '#003322', color: '#66bb6a' },
  ingest:  { bg: '#1a1a2e', color: '#7986cb' },
  azure:   { bg: '#2e1a0e', color: '#ffb74d' },
  general: { bg: '#1a1a1a', color: '#aaa' },
};

const Configuration = () => {
  const [config, setConfig] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [renderPolicy, setRenderPolicy] = useState(null);
  const [renderPolicyResult, setRenderPolicyResult] = useState(null);
  const [renderPolicySaving, setRenderPolicySaving] = useState(false);

  useEffect(() => {
    Promise.all([adminApi.getConfig(), adminApi.getRenderCachePolicy()]).then(([c, policy]) => {
      setConfig(c);
      setEdits(Object.fromEntries(c.map(item => [item.key, item.value])));
      setRenderPolicy(policy);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const save = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await adminApi.updateConfig(key, edits[key]);
      setConfig(c => c.map(item => item.key === key ? { ...item, value: edits[key] } : item));
    } catch (e) { console.error(e); }
    setSaving(s => ({ ...s, [key]: false }));
  };


  const saveRenderPolicy = async () => {
    setRenderPolicySaving(true);
    try {
      const payload = {
        policy: renderPolicy?.policy || 'representative',
        retention_days: Number(renderPolicy?.retention_days ?? 0),
        common_windows: Boolean(renderPolicy?.common_windows),
        include_mpr_fusion: renderPolicy?.include_mpr_fusion !== false,
        rollout_restart: Boolean(renderPolicy?.rollout_restart),
      };
      const result = await adminApi.updateRenderCachePolicy(payload);
      setRenderPolicy(result);
      setRenderPolicyResult(result);
    } catch (e) {
      console.error(e);
      setRenderPolicyResult({ error: e?.response?.data?.detail || e.message || 'Failed to save render cache policy' });
    }
    setRenderPolicySaving(false);
  };

  const categories = [...new Set(config.map(c => c.category))];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
        Configuration
      </Typography>
      <Typography sx={{ color: '#555', fontSize: 12, mb: 3 }}>
        Changes take effect immediately. Restart affected services for ingest/azure settings.
      </Typography>


      <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1, p: 2, mb: 3 }}>
        <Typography sx={{ color: '#00bcd4', fontSize: 16, fontWeight: 700, mb: 0.5 }}>
          Render Cache Policy
        </Typography>
        <Typography sx={{ color: '#777', fontSize: 12, mb: 2 }}>
          Save validates the policy, stores it, enqueues any missing matching render-cache jobs, and can request zero-downtime rolling restarts for the projection/warmup workers.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField select label="Policy" size="small" fullWidth value={renderPolicy?.policy || 'representative'}
              onChange={e => setRenderPolicy(p => ({ ...(p || {}), policy: e.target.value }))}
              InputLabelProps={{ sx: { color: '#777' } }} inputProps={{ style: { color: '#e0e0e0' } }}>
              <MenuItem value="representative">Representative</MenuItem>
              <MenuItem value="full-stack">Full stack</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Retention days" size="small" type="number" fullWidth value={renderPolicy?.retention_days ?? 0}
              helperText="0 = all demo studies" onChange={e => setRenderPolicy(p => ({ ...(p || {}), retention_days: e.target.value }))}
              inputProps={{ min: 0, max: 36500, style: { color: '#e0e0e0' } }} InputLabelProps={{ sx: { color: '#777' } }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Switch checked={Boolean(renderPolicy?.common_windows)} onChange={e => setRenderPolicy(p => ({ ...(p || {}), common_windows: e.target.checked }))} />}
              label={<Typography sx={{ color: '#aaa', fontSize: 12 }}>CT lung/soft windows</Typography>} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Switch checked={renderPolicy?.include_mpr_fusion !== false} onChange={e => setRenderPolicy(p => ({ ...(p || {}), include_mpr_fusion: e.target.checked }))} />}
              label={<Typography sx={{ color: '#aaa', fontSize: 12 }}>PET/CT fusion MPR</Typography>} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Switch checked={Boolean(renderPolicy?.rollout_restart)} onChange={e => setRenderPolicy(p => ({ ...(p || {}), rollout_restart: e.target.checked }))} />}
              label={<Typography sx={{ color: '#aaa', fontSize: 12 }}>Rolling restart workers</Typography>} />
          </Grid>
          <Grid item xs={12} md={12}>
            <Button variant="contained" disabled={renderPolicySaving} onClick={saveRenderPolicy}>Save & Apply</Button>
          </Grid>
        </Grid>
        {renderPolicyResult?.error && <Alert severity="error" sx={{ mt: 2 }}>{renderPolicyResult.error}</Alert>}
        {renderPolicyResult?.apply && <Alert severity="success" sx={{ mt: 2 }}>
          Enqueued {renderPolicyResult.apply.jobs_enqueued || 0} missing render-cache jobs ({renderPolicyResult.apply.mpr_fusion_jobs_enqueued || 0} PET/CT fusion MPR plane jobs). Queue: ready {renderPolicyResult.status?.queue?.ready || 0}, pending {renderPolicyResult.status?.queue?.pending || 0}, running {renderPolicyResult.status?.queue?.running || 0}, failed {renderPolicyResult.status?.queue?.failed || 0}.
        </Alert>}
      </Paper>

      {categories.map(cat => (
        <Box key={cat} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label={cat.toUpperCase()} size="small"
              sx={{ ...(CATEGORY_COLORS[cat] || CATEGORY_COLORS.general), borderRadius: 0, fontSize: 11, height: 20 }} />
          </Box>
          <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Key', 'Value', 'Description', ''].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: '#555', fontSize: 11, letterSpacing: 0.8, borderBottom: '1px solid #1e1e1e' }}>
                      {h.toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {config.filter(c => c.category === cat).map(item => {
                  const changed = edits[item.key] !== item.value;
                  return (
                    <TableRow key={item.key} sx={{ '&:hover': { bgcolor: '#141414' } }}>
                      <TableCell sx={{ color: '#aaa', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a', minWidth: 200 }}>
                        {item.key}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #1a1a1a', minWidth: 200 }}>
                        <TextField
                          size="small" value={edits[item.key] ?? ''}
                          onChange={e => setEdits(ed => ({ ...ed, [item.key]: e.target.value }))}
                          inputProps={{ style: { color: changed ? '#ffb74d' : '#e0e0e0', fontSize: 12, fontFamily: 'monospace', padding: '4px 8px' } }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: changed ? '#ffb74d44' : '#222' } } }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#555', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #1a1a1a', width: 40 }}>
                        {changed && (
                          <Tooltip title="Save">
                            <IconButton size="small" onClick={() => save(item.key)} disabled={saving[item.key]}
                              sx={{ color: '#ffb74d', '&:hover': { color: '#ffd54f' } }}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default Configuration;
